import React, { useEffect, useState } from 'react';
import { accessAPI, membershipAPI } from './api';
import ReferralAccess from './ReferralAccess';


const ACCESS_PATHS = [
  {
    title: 'Paid Membership',
    price: '$39/month',
    description: 'Full platform access while your Stripe Membership subscription is active.',
    status: 'Checkout is securely hosted by Stripe in test mode.',
    path: 'membership',
    action: 'Continue with Membership',
    pending: 'Membership selected. Continue to Stripe Checkout when you are ready.'
  },
  {
    title: 'Referral / Free Access',
    description: 'Full platform access after a manual eligibility review.',
    status: 'Secure evidence submission and manual review are available after selection.',
    path: 'referral',
    action: 'Select Referral',
    pending: 'Referral selected. Secure evidence submission and manual review are available below.'
  },
  {
    title: 'One-time AI Form Assist',
    description: 'Future assistance for one specific court form.',
    status: 'Purchasing is not enabled and the price is not confirmed.',
    path: 'form_assist',
    action: 'Select Form Assist',
    pending: 'Form Assist selected. No purchase or entitlement has been created.'
  }
];


const returnStateFromLocation = () => {
  const value = new URLSearchParams(window.location.search).get('membership_checkout');
  return ['processing', 'cancelled'].includes(value) ? value : null;
};


export default function AccessSelection({
  navigateToCheckout = (url) => window.location.assign(url)
}) {
  const [accessState, setAccessState] = useState(null);
  const [membershipStatus, setMembershipStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selecting, setSelecting] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState('');
  const [returnState] = useState(returnStateFromLocation);

  const loadMembershipStatus = async () => {
    const response = await membershipAPI.getStatus();
    setMembershipStatus(response.data);
    return response.data;
  };

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const response = await accessAPI.getState();
        if (!active) return;
        setAccessState(response.data);
        if (
          response.data.selection?.path === 'membership'
          || returnState === 'processing'
        ) {
          const statusResponse = await membershipAPI.getStatus();
          if (active) setMembershipStatus(statusResponse.data);
        }
      } catch (requestError) {
        if (active) {
          setError(requestError.response?.data?.detail || 'Unable to load your access selection.');
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [returnState]);

  useEffect(() => {
    if (returnState !== 'processing' || membershipStatus?.entitlement_active) return undefined;
    const poll = window.setInterval(() => {
      loadMembershipStatus().catch(() => {});
    }, 2500);
    return () => window.clearInterval(poll);
  }, [returnState, membershipStatus?.entitlement_active]);

  const referralDenied = accessState?.selection?.status === 'referral_denied';
  const selectedPath = accessState?.selection?.status === 'pending_next_step'
    ? accessState.selection.path
    : null;

  const refreshAccessState = async () => {
    const response = await accessAPI.getState();
    setAccessState(response.data);
  };

  const selectPath = async (path) => {
    setSelecting(path);
    setError('');
    try {
      const response = await accessAPI.selectPath(path);
      setAccessState(response.data);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Unable to save your access selection.');
    } finally {
      setSelecting(null);
    }
  };

  const continueMembership = async () => {
    setSelecting('membership');
    setError('');
    try {
      if (selectedPath !== 'membership') {
        const selectionResponse = await accessAPI.selectPath('membership');
        setAccessState(selectionResponse.data);
      }
      const checkoutResponse = await membershipAPI.createCheckout();
      navigateToCheckout(checkoutResponse.data.checkout_url);
    } catch (requestError) {
      setError(
        requestError.response?.data?.detail
        || 'Membership Checkout could not be started. You can safely try again.'
      );
    } finally {
      setSelecting(null);
    }
  };

  const openBillingPortal = async () => {
    setPortalLoading(true);
    setError('');
    try {
      const response = await membershipAPI.createPortal();
      navigateToCheckout(response.data.portal_url);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Billing management is temporarily unavailable.');
    } finally {
      setPortalLoading(false);
    }
  };

  const choosePath = (path) => (
    path === 'membership' ? continueMembership() : selectPath(path)
  );

  return (
    <section className="bg-gray-50 py-14 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        <div className="text-center max-w-3xl mx-auto">
          <p className="text-sm font-semibold uppercase tracking-wide text-orange-600">Account created</p>
          <h1 className="mt-3 text-3xl sm:text-4xl font-bold text-blue-950">Choose how you’ll access Good Parents Justice</h1>
          <p className="mt-4 text-gray-600">
            Your account is ready. Choose one path so we can save the correct next step.
          </p>
        </div>

        {returnState === 'processing' && !membershipStatus?.entitlement_active && (
          <div role="status" className="mt-6 bg-blue-50 border border-blue-200 text-blue-900 px-4 py-3 rounded">
            Payment is being confirmed from Stripe. This page will update after the signed webhook activates Membership.
          </div>
        )}
        {returnState === 'cancelled' && (
          <div role="status" className="mt-6 bg-amber-50 border border-amber-200 text-amber-900 px-4 py-3 rounded">
            Checkout was cancelled. No access was granted and you can safely try again.
          </div>
        )}
        {error && (
          <div role="alert" className="mt-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {loading && <p role="status" className="mt-8 text-center text-gray-600">Loading your access state…</p>}

        {!loading && (
          <div className={`mt-8 border rounded-xl px-5 py-4 text-center ${accessState?.has_full_access || membershipStatus?.entitlement_active ? 'bg-green-50 border-green-200 text-green-900' : 'bg-amber-50 border-amber-200 text-amber-900'}`}>
            {accessState?.has_full_access || membershipStatus?.entitlement_active
              ? 'Your full-platform access is active.'
              : referralDenied
                ? 'No protected feature access has been granted. The referral decision is final.'
                : 'No protected feature access has been granted. A pending selection does not provide access.'}
          </div>
        )}

        {membershipStatus?.entitlement_active && (
          <div className="mt-6 rounded-xl border border-green-200 bg-green-50 px-5 py-4 text-green-950">
            <h2 className="font-semibold">Membership active</h2>
            {membershipStatus.current_period_end && (
              <p className="mt-1 text-sm">Verified paid-through date: {new Date(membershipStatus.current_period_end).toLocaleDateString()}</p>
            )}
            {membershipStatus.cancel_at_period_end && (
              <p className="mt-2 text-sm">Cancellation is scheduled. Access remains active through the verified paid period.</p>
            )}
            {membershipStatus.billing_portal_available && (
              <button type="button" disabled={portalLoading} onClick={openBillingPortal} className="mt-3 px-4 py-2 rounded bg-blue-900 text-white disabled:bg-gray-400">
                {portalLoading ? 'Opening…' : 'Manage billing with Stripe'}
              </button>
            )}
          </div>
        )}

        {membershipStatus?.payment_attention_required && (
          <div role="status" className="mt-6 rounded-xl border border-amber-300 bg-amber-50 px-5 py-4 text-amber-950">
            Stripe reports that this Membership payment needs attention. Use billing management when available.
          </div>
        )}

        {referralDenied && (
          <div role="status" className="mt-6 rounded-xl border border-amber-300 bg-amber-50 px-5 py-4 text-amber-950">
            Your prior referral attempt was not approved. You may start a new referral attempt or choose another available access path.
          </div>
        )}

        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {ACCESS_PATHS.map((path) => {
            const anotherPathSelected = Boolean(selectedPath && selectedPath !== path.path);
            const membershipActive = path.path === 'membership' && membershipStatus?.entitlement_active;
            return (
              <article key={path.path} className={`bg-white border rounded-2xl shadow-sm p-6 flex flex-col text-left ${selectedPath === path.path ? 'border-orange-400' : 'border-gray-200'}`}>
                <h2 className="text-xl font-semibold text-blue-950">{path.title}</h2>
                {path.price && <p className="mt-2 text-2xl font-bold text-orange-600">{path.price}</p>}
                <p className="mt-3 text-gray-700 flex-1">{path.description}</p>
                <p className="mt-5 text-sm font-medium text-gray-600">
                  {referralDenied && path.path === 'referral'
                    ? 'The prior attempt received a final decision. You may start a new referral attempt.'
                    : selectedPath === path.path ? path.pending : path.status}
                </p>
                <button
                  type="button"
                  disabled={loading || Boolean(selecting) || anotherPathSelected || membershipActive}
                  onClick={() => choosePath(path.path)}
                  className="mt-5 w-full py-2 px-4 rounded-md bg-blue-900 text-white hover:bg-blue-800 disabled:bg-gray-200 disabled:text-gray-600 disabled:cursor-not-allowed"
                >
                  {selecting === path.path
                    ? (path.path === 'membership' ? 'Opening Stripe…' : 'Saving…')
                    : membershipActive
                      ? 'Membership active'
                      : referralDenied && path.path === 'referral'
                        ? 'Start New Referral Attempt'
                        : anotherPathSelected
                          ? 'Another path selected'
                          : path.action}
                </button>
              </article>
            );
          })}
        </div>

        {(selectedPath === 'referral' || referralDenied) && (
          <ReferralAccess
            key={accessState?.selection?.selection_id}
            onReturnToAccessOptions={selectedPath === 'referral'
              ? refreshAccessState
              : null}
          />
        )}
      </div>
    </section>
  );
}
